import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import { COVER_EKSPERIMENT_PRIS } from '@/state/GameState';
import type { ContractedBook } from '@/data/types';
import { animateModalGlide } from '@/ui/modalGlide';

const SHEET_WIDTH = 1000;
const ROW_WIDTH = 940;
const ROW_PADDING = 24;
const RAD_GAP = 16;
const RAD_HOYDE = 160;

export class DesignModal {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private scrollY = 0;
  private scrollMax = 0;

  constructor(private scene: Phaser.Scene) {}

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(120);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    this.scene.input.on('wheel', this.onWheel, this);
    this.render();
    animateModalGlide(this.scene, this.layer);
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.scene.input.off('wheel', this.onWheel, this);
    this.scrollY = 0;
    this.layer?.destroy(true);
    this.layer = undefined;
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const snap = gameState.snapshot;
    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 96;
    const sheetH = GAME_HEIGHT - 192;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, sheetH, COLORS.beige, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.6);
    this.layer.add(sheet);

    const topBorder = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, 4, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(topBorder);

    const headerY = sheetY + 36;
    const header = this.scene.add
      .text(sheetX + ROW_PADDING, headerY, 'Design — Cover-eksperiment', {
        fontFamily: FONTS.display,
        fontSize: fontStr(32),
        color: '#862736',
        wordWrap: { width: SHEET_WIDTH - ROW_PADDING * 2 - 110 },
      })
      .setOrigin(0, 0);
    this.layer.add(header);

    const beskrivelse = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        headerY + 76,
        'Gå utenfor boksen. 60% sjanse for å treffe et publikum, 40% sjanse for å bomme. Beslutningen tas nå, effekten kommer ved utgivelse.',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(20),
          color: '#5a4a3a',
          wordWrap: { width: SHEET_WIDTH - ROW_PADDING * 2 - 110 },
        },
      )
      .setOrigin(0, 0);
    this.layer.add(beskrivelse);

    const kapital = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        headerY + 196,
        `Dag ${snap.dag} · ${snap.kroner.toLocaleString('nb-NO')} kr`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#5a4a3a',
          fontStyle: '600',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(kapital);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - ROW_PADDING, headerY - 28, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const listX = sheetX + (SHEET_WIDTH - ROW_WIDTH) / 2;
    const listTop = headerY + 242;
    const listBunn = sheetY + sheetH - 32;
    const listHoyde = listBunn - listTop;

    const listContainer = this.scene.add.container(0, 0);
    this.layer.add(listContainer);

    const maske = this.scene.make.graphics({ x: 0, y: 0 }, false);
    maske.fillStyle(0xffffff);
    maske.fillRect(listX - 4, listTop, ROW_WIDTH + 8, listHoyde);
    const maskeObj = maske.createGeometryMask();
    listContainer.setMask(maskeObj);

    const boker = snap.iProduksjon.filter((b) => b.status === 'designes');

    let y = listTop - this.scrollY;
    let bygdHoyde = 0;

    if (boker.length === 0) {
      const tomBoks = this.scene.add
        .rectangle(listX, listTop, ROW_WIDTH, 180, COLORS.white, 0.9)
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.oakDark, 0.4);
      const tomTxt = this.scene.add
        .text(
          listX + ROW_WIDTH / 2,
          listTop + 90,
          'Ingen bøker i design-fasen.\nSend en redigert bok til design først.',
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(22),
            color: '#1a1a1a',
            align: 'center',
          },
        )
        .setOrigin(0.5);
      this.layer.add([tomBoks, tomTxt]);
    } else {
      for (const bok of boker) {
        const rad = this.byggBokRad(listX, y, bok, snap.dag, snap.kroner);
        listContainer.add(rad);
        y += RAD_HOYDE + RAD_GAP;
        bygdHoyde += RAD_HOYDE + RAD_GAP;
      }
    }

    this.scrollMax = Math.max(0, bygdHoyde - listHoyde);
    if (this.scrollY > this.scrollMax) this.scrollY = this.scrollMax;
    this.bindScroll(listX, listTop, listHoyde);
  }

  private bindScroll(listX: number, listTop: number, listHoyde: number): void {
    if (!this.layer) return;
    if (this.scrollMax <= 0) return;
    const sone = this.scene.add
      .rectangle(listX, listTop, ROW_WIDTH, listHoyde, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive();
    let dragStart = 0;
    let scrollStart = 0;
    sone.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragStart = p.y;
      scrollStart = this.scrollY;
    });
    sone.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown) return;
      const delta = dragStart - p.y;
      this.scrollY = Math.max(0, Math.min(this.scrollMax, scrollStart + delta));
      this.render();
    });
    this.layer.add(sone);
  }

  private onWheel = (
    _pointer: Phaser.Input.Pointer,
    _objs: Phaser.GameObjects.GameObject[],
    _dx: number,
    dy: number,
  ): void => {
    if (!this.layer || this.scrollMax <= 0) return;
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    this.render();
  };

  private byggBokRad(
    x: number,
    y: number,
    bok: ContractedBook,
    dag: number,
    kroner: number,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);

    const bg = this.scene.add
      .rectangle(0, 0, ROW_WIDTH, RAD_HOYDE, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.35);
    c.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(0, 0, 4, RAD_HOYDE, COLORS.ochre, 1)
      .setOrigin(0, 0);
    c.add(venstreBorder);

    const tittel = this.scene.add
      .text(ROW_PADDING, 16, bok.title, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
        wordWrap: { width: ROW_WIDTH - 320 },
      })
      .setOrigin(0, 0);
    c.add(tittel);

    const forfatter = this.scene.add
      .text(ROW_PADDING, 70, bok.author, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#5a4a3a',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);
    c.add(forfatter);

    const dagerIgjen =
      bok.faseFerdigDag !== null ? Math.max(0, bok.faseFerdigDag - dag) : 0;
    const meta = this.scene.add
      .text(ROW_PADDING, 110, `${dagerIgjen} dager igjen i design-fasen`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#5a4a3a',
      })
      .setOrigin(0, 0);
    c.add(meta);

    const eksperimentert = !!bok.coverEksperiment;
    const knappW = 280;
    const knappH = 66;
    const knappX = ROW_WIDTH - knappW - 16;
    const knappY = (RAD_HOYDE - knappH) / 2;
    const harRaad = kroner >= COVER_EKSPERIMENT_PRIS;

    if (eksperimentert) {
      const tagBg = this.scene.add
        .rectangle(knappX, knappY, knappW, knappH, COLORS.ochre, 1)
        .setOrigin(0, 0);
      const tagTxt = this.scene.add
        .text(knappX + knappW / 2, knappY + knappH / 2, 'Eksperiment lagret', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      c.add([tagBg, tagTxt]);
    } else {
      const fill = harRaad ? COLORS.ochre : 0xcfc6b7;
      const farge = harRaad ? '#ffffff' : '#7a7060';
      const tekst = harRaad
        ? `Eksperimenter (${COVER_EKSPERIMENT_PRIS.toLocaleString('nb-NO')} kr)`
        : `For lite (${COVER_EKSPERIMENT_PRIS.toLocaleString('nb-NO')} kr)`;
      const kBg = this.scene.add
        .rectangle(knappX, knappY, knappW, knappH, fill, 1)
        .setOrigin(0, 0);
      const kTxt = this.scene.add
        .text(knappX + knappW / 2, knappY + knappH / 2, tekst, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(17),
          color: farge,
          fontStyle: '700',
        })
        .setOrigin(0.5);
      c.add([kBg, kTxt]);
      if (harRaad) {
        kBg.setInteractive({ useHandCursor: true });
        kBg.on('pointerover', () => kBg.setAlpha(0.85));
        kBg.on('pointerout', () => kBg.setAlpha(1));
        kBg.on('pointerup', () => {
          const ok = gameState.kjoepCoverEksperiment(bok.isbn);
          if (ok) this.render();
        });
      }
    }

    return c;
  }
}
